import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import { createModalEscapeKeyDownHandler } from '../../../../utils/modalEscape';
import { PLAN_TYPE_KEYS, PLAN_TYPES, buildDefaultForm, getPlanType } from './rpmCarePlanConfig';
import {
  buildPrefillHtml,
  collectPlanPayload,
  formFromPlan,
  getEscalationBands,
  readGoal,
  scalePosition,
} from './rpmCarePlanUtils';
import './RpmCarePlan.css';

function Field({ label, required, children }) {
  return (
    <div className="rcp-field">
      <label>
        {label}
        {required ? <span className="rcp-req">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function CustomSelect({ label, options, isCustom, customValue, presetValue, onPreset, onCustom, onRevert }) {
  return (
    <div className="rcp-field">
      <label className="rcp-f-row">
        {label}
        {isCustom ? (
          <button type="button" className="rcp-revert" onClick={onRevert}>↩ presets</button>
        ) : null}
      </label>
      {isCustom ? (
        <input
          type="text"
          value={customValue}
          placeholder="Enter custom value"
          onChange={(e) => onCustom(e.target.value)}
        />
      ) : (
        <select
          value={presetValue}
          onChange={(e) => {
            if (e.target.value === '__custom__') onCustom('');
            else onPreset(e.target.value);
          }}
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
      )}
    </div>
  );
}

function Checklist({ items, onToggle, onRemove, onAdd, addPlaceholder }) {
  const [draft, setDraft] = useState('');
  const total = items.length;
  const n = items.filter((i) => i.checked).length;
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onAdd(v);
    setDraft('');
  };
  return (
    <>
      {items.map((item, idx) => (
        <label key={`${item.label}-${idx}`} className={`rcp-chk${item.checked ? ' on' : ''}`}>
          <input
            type="checkbox"
            checked={item.checked}
            onChange={() => onToggle(idx)}
          />
          <span>
            <span className="rcp-chk-t">
              {item.label}
              {item.custom ? <span className="rcp-chk-badge">CUSTOM</span> : null}
            </span>
            {item.desc ? <span className="rcp-chk-d">{item.desc}</span> : null}
          </span>
          {item.custom ? (
            <button type="button" className="rcp-rm" aria-label={`Remove ${item.label}`} onClick={(e) => { e.preventDefault(); onRemove(idx); }}>✕</button>
          ) : null}
        </label>
      ))}
      <div className="rcp-add">
        <input
          type="text"
          value={draft}
          placeholder={addPlaceholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="rcp-add-btn" onClick={add}>Add</button>
      </div>
      <div className="rcp-prog"><i style={{ width: `${total ? (n / total) * 100 : 0}%` }} /></div>
    </>
  );
}

const RpmCarePlanModal = ({
  isOpen,
  onClose,
  onSave,
  saving = false,
  editingPlan = null,
  patientInfo,
  sessionUser,
  providerName,
  providerOptions = [],
  latestVitals,
  availableTypeKeys = PLAN_TYPE_KEYS,
}) => {
  const [step, setStep] = useState('chooser');
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('');
  const [saved, setSaved] = useState(false);

  const chooserKeys = (availableTypeKeys && availableTypeKeys.length)
    ? availableTypeKeys
    : PLAN_TYPE_KEYS;

  useEffect(() => {
    if (!isOpen) return undefined;
    if (editingPlan) {
      setForm(formFromPlan(editingPlan));
      setStep('form');
      setStatus('Editing saved plan');
      setSaved(false);
    } else if (chooserKeys.length === 1) {
      const next = buildDefaultForm(chooserKeys[0]);
      if (chooserKeys[0] === 'weight' && latestVitals?.weight != null) {
        next.targetWeight = latestVitals.weight;
      }
      setForm(next);
      setStep('form');
      setStatus('');
      setSaved(false);
    } else {
      setForm(null);
      setStep('chooser');
      setStatus('');
      setSaved(false);
    }
    return undefined;
  }, [isOpen, editingPlan, chooserKeys.join(','), latestVitals?.weight]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const type = form ? getPlanType(form.typeKey) : null;
  const goal = form ? readGoal(form.typeKey, form) : {};
  const scaleValue = form?.typeKey === 'bp'
    ? Number(form.systolic) || 130
    : form?.typeKey === 'glucose'
      ? Number(form.fastingHigh) || 130
      : null;

  const prefillHtml = useMemo(
    () => (form ? buildPrefillHtml(form.typeKey, latestVitals || {}) : ''),
    [form, latestVitals]
  );

  if (!isOpen) return null;

  const patch = (partial) => {
    setForm((prev) => ({ ...prev, ...partial }));
    setSaved(false);
    setStatus('Unsaved changes');
  };

  const patchList = (key, updater) => {
    setForm((prev) => ({ ...prev, [key]: updater(prev[key] || []) }));
    setSaved(false);
    setStatus('Unsaved changes');
  };

  const startType = (key) => {
    const next = buildDefaultForm(key);
    if (key === 'weight' && latestVitals?.weight != null) {
      next.targetWeight = latestVitals.weight;
    }
    setForm(next);
    setStep('form');
    setStatus('');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!form) return;
    if (form.typeKey === 'custom' && !String(form.title || '').trim()) {
      setStatus('Enter a plan title first');
      setSaved(false);
      return;
    }
    const payload = collectPlanPayload(form, {
      patientId: patientInfo?.id,
      sessionUser,
      existingSignoff: editingPlan?.signoff,
    });
    const ok = await onSave(payload, editingPlan || null);
    if (ok) {
      setStatus(editingPlan ? 'Care plan updated' : 'Care plan saved');
      setSaved(true);
    }
  };

  const title = !form || step === 'chooser'
    ? 'Create Care Plan'
    : editingPlan
      ? (form.typeKey === 'custom' ? 'Edit Care Plan' : `Edit ${type.label} Care Plan`)
      : (form.typeKey === 'custom' ? 'Create Care Plan' : `Create ${type.label} Care Plan`);

  const intCount = form ? `${form.interventions.filter((i) => i.checked).length} of ${form.interventions.length}` : '';
  const eduCount = form ? `${form.education.filter((i) => i.checked).length} of ${form.education.length}` : '';

  return createPortal(
    <div
      className="rcp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={createModalEscapeKeyDownHandler(onClose)}
      role="presentation"
    >
      <div className="rcp-modal" role="dialog" aria-modal="true" aria-labelledby="rcpTitle">
        <div className="rcp-head">
          <div>
            <h2 id="rcpTitle">{title}</h2>
          </div>
          <button type="button" className="rcp-close" aria-label="Close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="rcp-scroll">
          {step === 'chooser' && (
            <div>
              <p className="rcp-lead">What kind of care plan do you want to create for this patient?</p>
              <div className="rcp-choose-grid">
                {chooserKeys.map((key) => {
                  const t = PLAN_TYPES[key];
                  return (
                    <button key={key} type="button" className="rcp-choice" onClick={() => startType(key)}>
                      <span>
                        <h4>{t.label}</h4>
                        <p>{t.blurb}</p>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'form' && form && type && (
            <>
              {prefillHtml ? (
                <div className="rcp-prefill">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span dangerouslySetInnerHTML={{ __html: prefillHtml }} />
                </div>
              ) : null}

              {form.typeKey === 'bp' && (
                <div className="rcp-card">
                  <div className="rcp-card-h"><h3>Blood pressure goal</h3></div>
                  <div className="rcp-grid">
                    <Field label="Target systolic" required>
                      <div className="rcp-pair">
                        <input type="number" min={90} max={180} value={form.systolic} onChange={(e) => patch({ systolic: e.target.value })} />
                        <span>/</span>
                        <input type="number" min={50} max={110} value={form.diastolic} onChange={(e) => patch({ diastolic: e.target.value })} />
                      </div>
                    </Field>
                    <Field label="Target basis">
                      <select value={form.basis} onChange={(e) => patch({ basis: e.target.value })}>
                        {type.basisOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Next plan review">
                      <AppDateInput value={form.reviewDate} onChange={(v) => patch({ reviewDate: v })} className="rcp-date-input" />
                    </Field>
                  </div>
                  {type.scale && (
                    <div className="rcp-scale">
                      <div className="rcp-bar">
                        {type.scale.segs.map(([label, cls]) => (
                          <div key={label} className={`rcp-seg ${cls}`}>{label}</div>
                        ))}
                        <div className="rcp-mk" data-l="Goal" style={{ left: `${scalePosition(scaleValue, type.scale.min, type.scale.max)}%` }} />
                        {latestVitals?.systolic != null ? (
                          <div
                            className="rcp-mk is-latest"
                            data-l={`Latest ${latestVitals.systolic}`}
                            style={{ left: `${scalePosition(latestVitals.systolic, type.scale.min, type.scale.max)}%` }}
                          />
                        ) : null}
                      </div>
                      <div className="rcp-axis">
                        {type.scale.axis.map((a) => <span key={a}>{a}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {form.typeKey === 'glucose' && (
                <div className="rcp-card">
                  <div className="rcp-card-h"><h3>Blood glucose goal</h3></div>
                  <div className="rcp-grid">
                    <Field label="Fasting target (mg/dL)">
                      <div className="rcp-pair">
                        <input type="number" min={60} max={130} value={form.fastingLow} onChange={(e) => patch({ fastingLow: e.target.value })} />
                        <span>–</span>
                        <input type="number" min={90} max={180} value={form.fastingHigh} onChange={(e) => patch({ fastingHigh: e.target.value })} />
                      </div>
                    </Field>
                    <Field label="Post-meal target (below, mg/dL)">
                      <input type="number" min={120} max={250} value={form.postMeal} onChange={(e) => patch({ postMeal: e.target.value })} />
                    </Field>
                    <Field label="A1c goal (%)">
                      <input type="number" min={5} max={12} step="0.1" value={form.a1c} onChange={(e) => patch({ a1c: e.target.value })} />
                    </Field>
                    <Field label="Diabetes type">
                      <select value={form.basis} onChange={(e) => patch({ basis: e.target.value })}>
                        {type.basisOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Next plan review">
                      <AppDateInput value={form.reviewDate} onChange={(v) => patch({ reviewDate: v })} className="rcp-date-input" />
                    </Field>
                  </div>
                  {type.scale && (
                    <div className="rcp-scale">
                      <div className="rcp-bar">
                        {type.scale.segs.map(([label, cls]) => (
                          <div key={label} className={`rcp-seg ${cls}`}>{label}</div>
                        ))}
                        <div className="rcp-mk" data-l="Goal" style={{ left: `${scalePosition(scaleValue, type.scale.min, type.scale.max)}%` }} />
                        {latestVitals?.glucose != null ? (
                          <div
                            className="rcp-mk is-latest"
                            data-l={`Latest ${latestVitals.glucose}`}
                            style={{ left: `${scalePosition(latestVitals.glucose, type.scale.min, type.scale.max)}%` }}
                          />
                        ) : null}
                      </div>
                      <div className="rcp-axis">
                        {type.scale.axis.map((a) => <span key={a}>{a}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {form.typeKey === 'weight' && (
                <div className="rcp-card">
                  <div className="rcp-card-h"><h3>Weight goal</h3></div>
                  <div className="rcp-grid">
                    <Field label="Target weight (lb)">
                      <input type="number" min={80} max={600} value={form.targetWeight} onChange={(e) => patch({ targetWeight: e.target.value })} />
                    </Field>
                    <Field label="Focus">
                      <select value={form.basis} onChange={(e) => patch({ basis: e.target.value })}>
                        {type.basisOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Alert if gain over 1 day (lb)">
                      <input type="number" min={1} max={10} value={form.dayGain} onChange={(e) => patch({ dayGain: e.target.value })} />
                    </Field>
                    <Field label="Alert if gain over 1 week (lb)">
                      <input type="number" min={1} max={20} value={form.weekGain} onChange={(e) => patch({ weekGain: e.target.value })} />
                    </Field>
                    <Field label="Next plan review">
                      <AppDateInput value={form.reviewDate} onChange={(v) => patch({ reviewDate: v })} className="rcp-date-input" />
                    </Field>
                  </div>
                </div>
              )}

              {form.typeKey === 'custom' && (
                <div className="rcp-card">
                  <div className="rcp-card-h"><h3>Plan details</h3></div>
                  <div className="rcp-grid">
                    <Field label="Plan title" required>
                      <input type="text" placeholder="e.g. COPD monitoring" value={form.title} onChange={(e) => patch({ title: e.target.value })} />
                    </Field>
                    <Field label="Condition / focus">
                      <input type="text" placeholder="e.g. COPD · J44.9" value={form.focus} onChange={(e) => patch({ focus: e.target.value })} />
                    </Field>
                    <Field label="Target metric">
                      <input type="text" placeholder="e.g. SpO2" value={form.metric} onChange={(e) => patch({ metric: e.target.value })} />
                    </Field>
                    <Field label="Target value">
                      <input type="text" placeholder="e.g. above 92%" value={form.target} onChange={(e) => patch({ target: e.target.value })} />
                    </Field>
                    <Field label="Next plan review">
                      <AppDateInput value={form.reviewDate} onChange={(v) => patch({ reviewDate: v })} className="rcp-date-input" />
                    </Field>
                  </div>
                  <div className="rcp-field" style={{ marginTop: 13 }}>
                    <label>Goal description</label>
                    <textarea placeholder="What are we trying to achieve for this patient?" value={form.description} onChange={(e) => patch({ description: e.target.value })} />
                  </div>
                </div>
              )}

              <div className="rcp-card">
                <div className="rcp-card-h"><h3>Monitoring protocol</h3></div>
                <div className="rcp-grid">
                  {form.typeKey === 'custom' ? (
                    <Field label="Device / method">
                      <input type="text" placeholder="e.g. Cellular pulse oximeter" value={form.device} onChange={(e) => patch({ device: e.target.value })} />
                    </Field>
                  ) : (
                    <Field label="Device">
                      <select value={form.device} onChange={(e) => patch({ device: e.target.value })}>
                        {type.deviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  )}
                  <CustomSelect
                    label={form.typeKey === 'glucose' ? 'Testing schedule' : form.typeKey === 'weight' ? 'Weigh-in schedule' : 'Reading schedule'}
                    options={type.scheduleOptions}
                    isCustom={form.scheduleIsCustom}
                    customValue={form.scheduleCustom}
                    presetValue={form.schedule}
                    onPreset={(v) => patch({ schedule: v, scheduleIsCustom: false })}
                    onCustom={(v) => patch({ scheduleIsCustom: true, scheduleCustom: v })}
                    onRevert={() => patch({ scheduleIsCustom: false, scheduleCustom: '', schedule: type.scheduleOptions[0] })}
                  />
                  <Field label="Required days per 30">
                    <input type="number" min={1} max={30} value={form.requiredDaysPer30} onChange={(e) => patch({ requiredDaysPer30: e.target.value })} />
                  </Field>
                  <CustomSelect
                    label="Missed-reading alert after"
                    options={type.gapOptions}
                    isCustom={form.gapIsCustom}
                    customValue={form.gapCustom}
                    presetValue={form.missedReadingAlert}
                    onPreset={(v) => patch({ missedReadingAlert: v, gapIsCustom: false })}
                    onCustom={(v) => patch({ gapIsCustom: true, gapCustom: v })}
                    onRevert={() => patch({ gapIsCustom: false, gapCustom: '', missedReadingAlert: type.gapOptions[0] })}
                  />
                </div>
              </div>

              <div className="rcp-card">
                <div className="rcp-card-h"><h3>{form.typeKey === 'custom' ? 'Alerts and escalation' : 'Alert thresholds and escalation'}</h3></div>
                {form.typeKey === 'custom' ? (
                  <textarea
                    placeholder="e.g. SpO2 below 90% -> call the care team the same day. Below 85% or breathless -> call 911."
                    value={form.escalation}
                    onChange={(e) => patch({ escalation: e.target.value })}
                  />
                ) : (
                  getEscalationBands(form.typeKey, goal).map((b) => (
                    <div key={b[1]} className="rcp-esc">
                      <span className="rcp-dot" style={{ background: b[0] }} />
                      <div>
                        <p className="rcp-esc-b" style={{ color: b[0] }}>{b[1]}</p>
                        <p className="rcp-esc-r">{b[2]}</p>
                        <p className="rcp-esc-a">{b[3]}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="rcp-card">
                <div className="rcp-card-h">
                  <h3>Interventions ordered</h3>
                  <span className="rcp-count">{intCount}</span>
                </div>
                <Checklist
                  items={form.interventions}
                  addPlaceholder="Add a custom intervention…"
                  onToggle={(idx) => patchList('interventions', (list) => list.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it))}
                  onRemove={(idx) => patchList('interventions', (list) => list.filter((_, i) => i !== idx))}
                  onAdd={(label) => patchList('interventions', (list) => [...list, { label, desc: '', checked: true, custom: true }])}
                />
              </div>

              <div className="rcp-card">
                <div className="rcp-card-h">
                  <h3>Education and teach-back</h3>
                  <span className="rcp-count">{eduCount}</span>
                </div>
                <Checklist
                  items={form.education}
                  addPlaceholder="Add a custom education topic…"
                  onToggle={(idx) => patchList('education', (list) => list.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it))}
                  onRemove={(idx) => patchList('education', (list) => list.filter((_, i) => i !== idx))}
                  onAdd={(label) => patchList('education', (list) => [...list, { label, desc: '', checked: true, custom: true }])}
                />
              </div>

              <div className="rcp-card">
                <div className="rcp-card-h"><h3>Clinical notes</h3></div>
                <textarea
                  placeholder="Document anything the care team should know."
                  value={form.notes}
                  onChange={(e) => patch({ notes: e.target.value })}
                />
              </div>

              <div className="rcp-card">
                <div className="rcp-card-h"><h3>Review &amp; sign-off</h3></div>
                <div className="rcp-grid">
                  <Field label="Created by">
                    <input
                      type="text"
                      className="rcp-readonly"
                      readOnly
                      value={editingPlan?.signoff?.createdBy || sessionUser?.username || sessionUser?.name || ''}
                    />
                  </Field>
                  <Field label="Approved by" required>
                    <select value={form.approvedBy} onChange={(e) => patch({ approvedBy: e.target.value })}>
                      <option value="__pending__">Pending approval</option>
                      {(providerOptions.length ? providerOptions : (providerName ? [providerName] : [])).map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                      {form.approvedBy && form.approvedBy !== '__pending__'
                        && !(providerOptions.length ? providerOptions : [providerName]).includes(form.approvedBy) ? (
                        <option value={form.approvedBy}>{form.approvedBy}</option>
                      ) : null}
                    </select>
                  </Field>
                  <Field label="Created date">
                    <input
                      type="text"
                      className="rcp-readonly"
                      readOnly
                      value={editingPlan?.signoff?.createdDate
                        ? new Date(editingPlan.signoff.createdDate).toLocaleDateString('en-US')
                        : new Date().toLocaleDateString('en-US')}
                    />
                  </Field>
                </div>
              </div>
            </>
          )}
        </div>

        {step === 'form' && (
          <div className="rcp-foot">
            {!editingPlan && chooserKeys.length > 1 ? (
              <button type="button" className="rcp-btn back" onClick={() => { setStep('chooser'); setForm(null); }}>
                ‹ Change type
              </button>
            ) : <span className="rcp-status" />}
            <span className={`rcp-status${saved ? ' is-saved' : ''}`}>{status === 'Draft — not saved' ? '' : status}</span>
            <button
              type="button"
              className="rcp-btn ghost"
              onClick={() => onSave && onSave(collectPlanPayload(form, { patientId: patientInfo?.id, sessionUser, existingSignoff: editingPlan?.signoff }), editingPlan, { action: 'email' })}
            >
              Email
            </button>
            <button
              type="button"
              className="rcp-btn ghost"
              onClick={() => onSave && onSave(collectPlanPayload(form, { patientId: patientInfo?.id, sessionUser, existingSignoff: editingPlan?.signoff }), editingPlan, { action: 'print' })}
            >
              Print
            </button>
            <button type="button" className="rcp-btn primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : (editingPlan ? 'Update care plan' : 'Save care plan')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default RpmCarePlanModal;
